import { useRef, useState } from "react";
import { DoorOpen, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, dt, errMsg, uid } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePoll from "@/hooks/usePoll";
import { useVerifier, VerifyInput, VerifyResult } from "@/components/PermitVerifier";
import { Modal, PageHeader } from "@/components/common";

export default function Gate() {
  const { can } = useAuth();
  const v = useVerifier();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exitErr, setExitErr] = useState("");
  const [done, setDone] = useState(null);
  const keys = useRef({});
  const recent = usePoll(can("confirm_exit") ? "/exits" : null, null, 20000);
  const p = v.res?.permit;
  const blockedByDelivery = p && p.require_delivery_before_exit && !p.delivered_at;

  const doExit = async () => {
    setBusy(true); setExitErr("");
    keys.current[p.id] = keys.current[p.id] || uid();
    try {
      const { data } = await api.post(`/permits/${p.id}/exit`, { idempotency_key: keys.current[p.id] });
      setDone({ ear_tag: p.ear_tag, number: p.number, ...data });
      toast.success("تم تسجيل الخروج الفعلي بنجاح");
      setConfirm(false); v.setRes(null); recent.reload();
    } catch (e) {
      setExitErr(errMsg(e) + (e.response ? "" : " – يمكنك إعادة المحاولة بأمان، لن يُسجل الخروج مرتين."));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <PageHeader title="بوابة الخروج" subtitle="امسح رمز QR أو أدخل رقم التصريح. المسح وحده لا يسجل الخروج." />
      {done && (
        <div className="rounded-2xl bg-emerald-600 text-white p-6 text-center fade-up" data-testid="exit-success">
          <div className="text-2xl font-black font-display">تم تأكيد خروج الكبش رقم <span className="num">{done.ear_tag}</span></div>
          <div className="mt-1">التصريح {done.number} – {dt(done.exited_at)} – {done.exited_by}</div>
          <button className="k-btn bg-white text-emerald-700 mt-4" onClick={() => setDone(null)} data-testid="exit-next-btn">التحقق من تصريح آخر</button>
        </div>
      )}
      {!done && <VerifyInput v={v} />}
      <VerifyResult res={v.res}>
        {v.res?.result === "valid" && can("confirm_exit") && (
          <div className="space-y-2">
            {blockedByDelivery && <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-amber-900 font-bold" data-testid="exit-needs-delivery">لم يُسجل تسليم الكبش داخل الحظيرة بعد – يجب أن يؤكد عامل الحظيرة التسليم أولًا</div>}
            <button className="k-btn-primary w-full h-16 text-xl" disabled={blockedByDelivery} onClick={() => setConfirm(true)} data-testid="confirm-exit-button"><DoorOpen className="w-6 h-6" /> تأكيد الخروج الفعلي</button>
          </div>
        )}
      </VerifyResult>
      <Modal open={confirm} onOpenChange={setConfirm} title="تأكيد الخروج الفعلي" description="اضغط على التأكيد فقط بعد خروج الكبش فعلًا من البوابة ومطابقة رقم الكبش"
        footer={<>
          <button className="k-btn-primary h-14 text-lg" onClick={doExit} disabled={busy} data-testid="confirm-exit-final">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : exitErr ? <RotateCcw className="w-5 h-5" /> : <DoorOpen className="w-5 h-5" />} {exitErr ? "إعادة المحاولة" : "نعم، خرج الكبش"}</button>
          <button className="k-btn-outline h-14" onClick={() => setConfirm(false)} data-testid="confirm-exit-cancel">تراجع</button>
        </>}>
        {p && <div className="text-center"><div className="text-sm text-slate-500">رقم الكبش</div><div className="text-6xl font-black num text-[#1E40AF]">{p.ear_tag}</div></div>}
        {exitErr && <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-800 p-3 text-sm font-bold" data-testid="exit-error">{exitErr}</div>}
      </Modal>
      {recent.data?.length > 0 && (
        <div className="k-card p-5" data-testid="recent-exits">
          <div className="font-display font-bold mb-2">آخر عمليات الخروج</div>
          <ul className="divide-y text-sm">{recent.data.slice(0, 10).map((e) => <li key={e.id} className="py-2 flex justify-between"><span><b className="num">{e.ear_tag}</b> – {e.permit_number}</span><span className="text-slate-500">{dt(e.exited_at)} · {e.guard_name}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}
